import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeleteIcon from '@mui/icons-material/Delete';
import { Helmet } from 'react-helmet-async';
import {
  superAdminApi,
  type CreateUserDto,
} from '../../api/superAdminApi';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { User, Unit } from '../../types/domain';

const ROLE_LABELS = {
  SUPER_ADMIN: 'Super Admin',
  UNIT_ADMIN: 'Admin jednotky',
  RECORDER: 'Zapisovatel',
} as const;

export const UsersPage = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [creating, setCreating] = useState(false);
  const [resetPwd, setResetPwd] = useState<User | null>(null);
  const [deactivating, setDeactivating] = useState<User | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['superadmin', 'users'],
    queryFn: superAdminApi.listUsers,
  });
  const { data: units = [] } = useQuery({
    queryKey: ['superadmin', 'units'],
    queryFn: superAdminApi.listUnits,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });

  const createMutation = useMutation({
    mutationFn: superAdminApi.createUser,
    onSuccess: () => {
      enqueueSnackbar('Uživatel vytvořen', { variant: 'success' });
      invalidate();
      setCreating(false);
    },
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při vytváření', { variant: 'error' }),
  });

  const resetMutation = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      superAdminApi.resetUserPassword(id, password),
    onSuccess: () => {
      enqueueSnackbar('Heslo změněno', { variant: 'success' });
      setResetPwd(null);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => superAdminApi.deactivateUser(id),
    onSuccess: () => {
      enqueueSnackbar('Uživatel deaktivován', { variant: 'success' });
      invalidate();
      setDeactivating(null);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const columns: GridColDef<User>[] = useMemo(
    () => [
      { field: 'username', headerName: 'Username', width: 160 },
      { field: 'displayName', headerName: 'Jméno', flex: 1, minWidth: 180 },
      { field: 'email', headerName: 'E-mail', width: 200 },
      {
        field: 'role',
        headerName: 'Role',
        width: 160,
        renderCell: ({ row }) => (
          <Chip
            label={ROLE_LABELS[row.role]}
            size="small"
            color={row.role === 'SUPER_ADMIN' ? 'error' : row.role === 'UNIT_ADMIN' ? 'primary' : 'default'}
          />
        ),
      },
      { field: 'unitName', headerName: 'Jednotka', width: 180, renderCell: ({ row }) => row.unitName ?? '—' },
      {
        field: 'isActive',
        headerName: 'Stav',
        width: 110,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={row.isActive ? 'Aktivní' : 'Neaktivní'}
            color={row.isActive ? 'success' : 'default'}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Akce',
        width: 140,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row">
            <Tooltip title="Reset hesla">
              <IconButton size="small" onClick={() => setResetPwd(row)}>
                <LockResetIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={row.isActive ? 'Deaktivovat' : 'Neaktivní'}>
              <span>
                <IconButton size="small" color="error" disabled={!row.isActive} onClick={() => setDeactivating(row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <Helmet>
        <title>Uživatelé | SUPER ADMIN</title>
      </Helmet>
      <PageHeader
        title="Uživatelé"
        subtitle="Správa všech uživatelů napříč jednotkami"
        actions={
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreating(true)}>
            Nový uživatel
          </Button>
        }
      />
      <Box sx={{ height: 'calc(100vh - 220px)', bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid rows={users} columns={columns} loading={isLoading} disableRowSelectionOnClick sx={{ border: 0 }} />
      </Box>

      <CreateUserDialog
        open={creating}
        units={units}
        loading={createMutation.isPending}
        onCancel={() => setCreating(false)}
        onSubmit={(dto) => createMutation.mutate(dto)}
      />

      <ResetPasswordDialog
        open={!!resetPwd}
        user={resetPwd}
        loading={resetMutation.isPending}
        onCancel={() => setResetPwd(null)}
        onSubmit={(password) => resetPwd && resetMutation.mutate({ id: resetPwd.id, password })}
      />

      <ConfirmDialog
        open={!!deactivating}
        title="Deaktivovat uživatele?"
        message={`Uživatel "${deactivating?.displayName}" bude označen jako neaktivní a nebude se moci přihlásit.`}
        destructive
        loading={deactivateMutation.isPending}
        onCancel={() => setDeactivating(null)}
        onConfirm={() => deactivating && deactivateMutation.mutate(deactivating.id)}
      />
    </>
  );
};

interface CreateUserDialogProps {
  open: boolean;
  units: Unit[];
  loading: boolean;
  onCancel: () => void;
  onSubmit: (dto: CreateUserDto) => void;
}

const CreateUserDialog = ({ open, units, loading, onCancel, onSubmit }: CreateUserDialogProps) => {
  const [form, setForm] = useState<CreateUserDto>({
    username: '',
    password: '',
    displayName: '',
    email: '',
    role: 'UNIT_ADMIN',
    unitId: null,
  });
  const update = (field: keyof CreateUserDto, value: any) => setForm((f) => ({ ...f, [field]: value }));

  const reset = () =>
    setForm({ username: '', password: '', displayName: '', email: '', role: 'UNIT_ADMIN', unitId: null });

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEnter: reset }}
    >
      <DialogTitle>Nový uživatel</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Username"
            value={form.username}
            onChange={(e) => update('username', e.target.value)}
            required
            autoFocus
            inputProps={{ pattern: '[a-zA-Z0-9._-]+' }}
          />
          <TextField
            label="Zobrazované jméno"
            value={form.displayName}
            onChange={(e) => update('displayName', e.target.value)}
            required
          />
          <TextField
            label="E-mail"
            type="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
          />
          <TextField
            label="Heslo"
            type="password"
            value={form.password}
            onChange={(e) => update('password', e.target.value)}
            required
            helperText="Alespoň 8 znaků"
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={form.role}
              label="Role"
              onChange={(e) => update('role', e.target.value)}
            >
              <MenuItem value="SUPER_ADMIN">Super Admin (bez jednotky)</MenuItem>
              <MenuItem value="UNIT_ADMIN">Admin jednotky</MenuItem>
              <MenuItem value="RECORDER">Zapisovatel</MenuItem>
            </Select>
          </FormControl>
          {form.role !== 'SUPER_ADMIN' && (
            <FormControl fullWidth required>
              <InputLabel>Jednotka</InputLabel>
              <Select
                value={form.unitId ?? ''}
                label="Jednotka"
                onChange={(e) => update('unitId', e.target.value ? Number(e.target.value) : null)}
              >
                {units.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Zrušit</Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(form)}
          disabled={
            loading ||
            !form.username ||
            !form.password ||
            !form.displayName ||
            (form.role !== 'SUPER_ADMIN' && !form.unitId)
          }
        >
          Vytvořit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ResetPasswordDialogProps {
  open: boolean;
  user: User | null;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (password: string) => void;
}

const ResetPasswordDialog = ({ open, user, loading, onCancel, onSubmit }: ResetPasswordDialogProps) => {
  const [password, setPassword] = useState('');
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth TransitionProps={{ onEnter: () => setPassword('') }}>
      <DialogTitle>Reset hesla</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Uživatel" value={user?.displayName ?? ''} disabled />
          <TextField
            label="Nové heslo"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            helperText="Alespoň 8 znaků"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Zrušit</Button>
        <Button variant="contained" onClick={() => onSubmit(password)} disabled={loading || password.length < 8}>
          Změnit heslo
        </Button>
      </DialogActions>
    </Dialog>
  );
};
